import { ReadonlyVec2, ReadonlyVec3, vec3, vec4 } from 'gl-matrix';
import { AdaptiveSphere } from '~/engine/data/common/customs/AdaptiveSphere';
import { TargetSphere } from '~/engine/data/common/customs/TargetSphere';
import { Environment } from '~/engine/data/Environment';
import { ImageObject } from '~/engine/data/image/ImageObject';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import {
    BrushCursor,
    BrushCursorShape,
} from '~/engine/tool/actions/contouring-actions/BrushCursor';
import { BrushMode } from '~/engine/tool/actions/contouring-actions/BrushStamp';
import { BrushStroke } from '~/engine/tool/actions/contouring-actions/BrushStroke';
import {
    adaptiveStrokeRefusal,
    DrawingConstraints,
    intensityStrokeRefusal,
    sampledImageOf,
    strokeFollowsThreshold,
} from '~/engine/tool/actions/DrawActionState';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '~/engine/tool/EventHandlerResult';
import { ModifierKeys } from '~/engine/tool/ModifierKeys';
import {
    MOUSE_BUTTON_LEFT,
    MOUSE_BUTTONS_NONE,
} from '~/engine/tool/MouseButton';
import { CrosshairInteractionMode, Tool } from '~/engine/tool/Tool';
import { TextureView } from '~/engine/view/TextureView';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { StrokeMode } from '~/state/atoms/toolsAtoms';
import { enqueueSnackbar } from '~/utils/notifications';
import { writeRefusal } from './editableView';
import { localization } from '~/i18n';

/** Cursor color while the brush erases. */
const ERASE_CURSOR_COLOR: ReadonlyVec3 = vec3.fromValues(1, 0, 0);

/**
 * {@link Tool} that draws on, or erases from, a structure's contours with a
 * brush of a given diameter. Its {@link BrushMode} decides what one stamp
 * of the brush covers.
 *
 * - A stroke starts on a left press in a view of the armed environment,
 *   draws a stamp at every mouse move, and is committed on release or
 *   deactivation. Escape abandons it. While in progress it is shown on
 *   every view displaying the image.
 * - A stroke runs in the {@link strokeMode} selected at the press: confined
 *   to the displayed slice in 2D, a sphere of the brush diameter in 3D.
 * - A press, or a drag with no stroke in progress, on a view the
 *   brush refuses ({@link refusalFor}) warns with the reason: a view of
 *   any other environment, in 2D a view that follows no image slice, or,
 *   while the stroke follows image intensity, a view whose image it cannot
 *   follow ({@link intensityRefusal}). An adaptive stroke follows the image
 *   the view shows, which on a locked row is that row's own image. A tool
 *   armed with no environment refuses every view.
 * - A stroke takes moves from the view it started on only; the release
 *   commits it wherever it happens.
 * - Holding {@link BRUSH_ERASE_MODIFIER_KEYS} on the press inverts erasing
 *   for that stroke only.
 * - Between strokes the brush outline follows the mouse; it is red while
 *   the brush erases.
 * - {@link BRUSH_SIZE_MODIFIER_KEYS} + wheel resizes the brush.
 */
export class BrushTool extends Tool {
    public readonly toolType = 'brush';

    public static readonly BRUSH_SIZE_MODIFIER_KEYS = ModifierKeys.SHIFT;

    public static readonly BRUSH_ERASE_MODIFIER_KEYS = ModifierKeys.ALT;

    /** Maximum diameter of the brush in millimeters. */
    public static readonly MAX_BRUSH_SIZE = 40;

    private readonly outline: BrushCursor;

    private stroke: BrushStroke | undefined;

    /** Diameter of the brush in millimeters. */
    private _diameter: number;

    private _strokeMode: StrokeMode;

    /**
     * @param mode What one stamp of the brush covers
     * @param image Image the brush draws on the grid of
     * @param structure Structure the strokes are committed onto
     * @param environment Environment whose views the brush draws on;
     *     `undefined` refuses every view
     * @param erase Whether a stroke erases unless inverted by
     *     {@link BRUSH_ERASE_MODIFIER_KEYS}
     * @param strokeMode Whether a stroke is confined to the displayed
     *     slice or is a sphere of the brush diameter
     * @param diameter Brush diameter in millimeters
     * @param brushSizeChangedCallback Told the new diameter after a wheel
     *     resize
     * @param constraints Constraints applied to every drawing stroke
     */
    public constructor(
        private readonly mode: BrushMode,
        private readonly image: ImageObject,
        private readonly structure: RegionOfInterest,
        private readonly environment: Environment | undefined,
        private readonly erase: boolean,
        strokeMode: StrokeMode,
        diameter: number,
        private readonly brushSizeChangedCallback: (brushSize: number) => void,
        private readonly constraints?: DrawingConstraints
    ) {
        super();
        this._strokeMode = strokeMode;
        this._diameter = diameter;
        this.outline = new BrushCursor(
            (view, coordinates, size) =>
                this.createCursorShape(view, coordinates, size),
            diameter
        );
    }

    /**
     * Diameter of the brush in millimeters. A value outside
     * (1, {@link MAX_BRUSH_SIZE}] is ignored.
     */
    public get diameter(): number {
        return this._diameter;
    }

    public set diameter(diameter: number) {
        if (diameter > 1 && diameter < BrushTool.MAX_BRUSH_SIZE + 1e-4) {
            this._diameter = diameter;
            this.outline.resize(diameter);
        }
    }

    public get strokeMode(): StrokeMode {
        return this._strokeMode;
    }

    /**
     * Takes effect on the next stroke; a stroke in progress keeps its mode.
     * The outline is rebuilt so it previews the new mode, or hidden when
     * the brush refuses the view it is on in that mode.
     */
    public set strokeMode(strokeMode: StrokeMode) {
        if (strokeMode === this._strokeMode) {
            return;
        }
        this._strokeMode = strokeMode;
        const shownOn = this.outline.shownOn;
        if (shownOn && this.refusalFor(shownOn) !== undefined) {
            this.outline.hide();
        } else {
            this.outline.refresh();
        }
    }

    /**
     * Resize the brush when {@link BRUSH_SIZE_MODIFIER_KEYS} are held, in
     * steps that coarsen with the diameter, and report the new diameter.
     */
    public onMouseWheel(
        delta: 1 | -1,
        modifierKeys: ModifierKeys,
        _coordinates: ReadonlyVec2,
        _view: PlaneView
    ): EventHandlerResult {
        if (!modifierKeys.equals(BrushTool.BRUSH_SIZE_MODIFIER_KEYS)) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        let step: number;
        if (this._diameter <= 5) {
            step = 1;
        } else if (this._diameter <= 10) {
            step = 2;
        } else if (this._diameter <= 20) {
            step = 3;
        } else {
            step = 5;
        }
        this.diameter = Math.max(
            Math.min(this._diameter + delta * step, BrushTool.MAX_BRUSH_SIZE),
            1.0
        );
        this.brushSizeChangedCallback(this._diameter);
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    public override isActionInProgress(): boolean {
        return this.stroke !== undefined;
    }

    public override getCrosshairInteractionMode(
        isDrawingActiveInOtherView: boolean
    ): CrosshairInteractionMode {
        return Tool.contouringCrosshairMode(
            isDrawingActiveInOtherView,
            this.isStrokeInProgress()
        );
    }

    public override getDrawingView(): PlaneView | undefined {
        return this.stroke?.view;
    }

    public onMousePress(
        button: number,
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: PlaneView
    ): EventHandlerResult {
        if (this.structure.canModifyContours === false) {
            enqueueSnackbar(
                'Modifying approved structures is not allowed. Unapprove the structure to make changes.',
                { variant: 'warning' }
            );
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        if (this.stroke) {
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        if (button !== MOUSE_BUTTON_LEFT) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        const erase =
            this.erase !==
            modifierKeys.equals(BrushTool.BRUSH_ERASE_MODIFIER_KEYS);
        // The inverted stroke is checked too: an eraser inverted to draw
        // takes on the threshold.
        const refusal =
            this.refusalFor(view) ?? this.intensityRefusal(view, erase);
        if (refusal !== undefined) {
            enqueueSnackbar(refusal, { variant: 'warning' });
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        view.snapToImagePixel();
        this.stroke = this.startStroke(view, coordinates, erase);
        this.outline.hide();
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    /**
     * A view of another environment, or with none armed, gets the refusal
     * of {@link writeRefusal}; in 2D a view that follows no image slice the
     * `contouring.refused.offImageSlice` reason, since a 2D stroke has no
     * slice to stay on there (a 3D stroke runs on any view); and, while
     * the brush's own stroke follows intensity, the refusal of
     * {@link intensityRefusal}.
     */
    public override refusalFor(view: PlaneView): string | undefined {
        return writeRefusal(view, this.environment, (own) =>
            this._strokeMode === '2d' && own.imageSliceAxis === undefined
                ? localization.t('contouring.refused.offImageSlice')
                : this.intensityRefusal(own, this.erase)
        );
    }

    /**
     * The refusal of a stroke that follows intensity: the threshold, which
     * applies to a drawing stroke only, needs the primary image
     * ({@link intensityStrokeRefusal}); the adaptive stamp, drawing or
     * erasing, needs the view to show an image
     * ({@link adaptiveStrokeRefusal}).
     */
    private intensityRefusal(
        view: PlaneView,
        erase: boolean
    ): string | undefined {
        if (strokeFollowsThreshold(this.constraints, erase)) {
            return intensityStrokeRefusal(view);
        }
        return this.mode === 'smart' ? adaptiveStrokeRefusal(view) : undefined;
    }

    /**
     * Continue the stroke in progress, or move the brush outline when none
     * is. A move with no button held abandons the stroke: its release was
     * not seen. The outline follows the mouse on the views the brush
     * accepts only, and leaves a view it refuses.
     */
    public onMouseMove(
        coordinates: ReadonlyVec2,
        buttons: number,
        view: TextureView
    ): EventHandlerResult {
        if (this.stroke && buttons !== MOUSE_BUTTONS_NONE) {
            return this.stroke.onMouseMove(coordinates, buttons, view);
        }
        this.discardStroke();
        if (!(view instanceof PlaneView) || !this.structure.canModifyContours) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        const refusal = this.refusalFor(view);
        if (refusal !== undefined) {
            this.outline.hide();
            // A drag across a refused view would otherwise pass with no
            // feedback at all; the global snackbar preventDuplicate config
            // (see index.tsx) collapses the repeats while one is still
            // shown.
            if (buttons !== MOUSE_BUTTONS_NONE) {
                enqueueSnackbar(refusal, { variant: 'warning' });
            }
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        if (buttons === MOUSE_BUTTONS_NONE) {
            this.outline.moveTo(view, coordinates);
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public onMouseRelease(
        _button: number,
        _coordinates: ReadonlyVec2,
        _view: TextureView
    ): EventHandlerResult {
        return this.commitStroke();
    }

    public override onKeyPress(
        key: string,
        _modifierKeys: ModifierKeys,
        _view: TextureView
    ): EventHandlerResult {
        if (key !== 'Escape' || !this.stroke) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        this.discardStroke();
        return new EventHandlerResult(
            EventHandlerResultType.EVENT_HANDLED,
            true
        );
    }

    /** Drop the stroke in progress, if any, without committing it. */
    private discardStroke(): void {
        const stroke = this.stroke;
        if (!stroke) {
            return;
        }
        this.stroke = undefined;
        stroke.clearMemory();
    }

    public onDeactivate(): EventHandlerResult {
        this.outline.hide();
        if (this.stroke) {
            return this.commitStroke();
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    /**
     * Start a stroke with the brush's current settings.
     * @param coordinates Client pixel coordinates of the press
     * @param erase Whether the stroke erases
     */
    protected startStroke(
        view: PlaneView,
        coordinates: ReadonlyVec2,
        erase: boolean
    ): BrushStroke {
        return new BrushStroke(view, this.structure, this.image, coordinates, {
            mode: this.mode,
            diameter: this._diameter,
            erase,
            strokeMode: this._strokeMode,
            constraints: this.constraints,
        });
    }

    private commitStroke(): EventHandlerResult {
        const stroke = this.stroke;
        if (!stroke) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        this.stroke = undefined;
        return stroke.commit();
    }

    /**
     * The shape the brush outline shows on `view`, for the brush's mode.
     * @param coordinates Client pixel coordinates of the mouse
     * @param diameter Brush diameter in millimeters
     */
    protected createCursorShape(
        view: PlaneView,
        coordinates: ReadonlyVec2,
        diameter: number
    ): BrushCursorShape {
        const rgb = this.erase ? ERASE_CURSOR_COLOR : this.structure.color;
        const color = vec4.fromValues(rgb[0], rgb[1], rgb[2], 1);
        switch (this.mode) {
            case 'static':
                return new TargetSphere(
                    this.image,
                    view.clientToPatientCoordinates(coordinates),
                    diameter,
                    color
                );
            case 'smart':
                // The smart outline is filled from the image slice under
                // the cursor, so the view is put on one first.
                view.snapToImagePixel();
                return new AdaptiveSphere(
                    sampledImageOf(view, this.image),
                    view.clientToPatientCoordinates(coordinates),
                    diameter,
                    view.rotation,
                    this._strokeMode,
                    color
                );
        }
    }
}
